import React, { useCallback, useEffect, useState } from 'react'
import { FilledButton } from '../../common/buttons/FilledButton'
import { SimpleButton } from '../../common/buttons/SimpleButton'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { RecurrenceInterval } from '../../../../types/recurring-interval'
import { translations } from '../../../../utils/translations/translations-manager'
import { RecurrenceTranslations } from '@cents/translations-utils'
import { useSubscriptionChange } from '../../../../hooks/contracts/subscriptions/SubscriptionChangeHook'
import { ContractRecurrenceInput } from '../inputs/ContractRecurrenceInput'
import { Recurrence } from '../../../../types/donation/recurrence'
import { Contract } from '../../../../types/contract'

type ValidationErrorKeys = 'same_as_before' | 'invalid'
const validationErrors: Record<ValidationErrorKeys, () => string> = {
  same_as_before: () => translations.texts.recurrence_same_as_before_error.toString(),
  invalid: () => translations.texts.invalid_contract_recurrence.toString(),
}
const errorMessages: Record<'save_error', () => string> = {
  save_error: () => translations.texts.contract_update_error.toString(),
}
export function SubscriptionRecurrenceChange({
  contract,
  success,
  complete,
  cancel,
}: {
  contract: Contract
  success: () => void
  complete: () => void
  cancel: () => void
}): JSX.Element {
  if (contract.charityId === undefined || contract._id === undefined) throw new Error('Charity ID and Contract ID must be defined')
  const [initialValue] = useState<Recurrence | undefined>(contract.subscriptionRecurrence ? { interval: contract.subscriptionRecurrence, count: contract.subscriptionRecurrenceCount ?? 1 } : undefined)
  const [recurrenceInterval, setRecurrenceInterval] = useState<Recurrence | undefined>(initialValue)
  const [validationError, setValidationError] = useState<'same_as_before' | 'invalid' | undefined>()
  const { save, error, clearError, state } = useSubscriptionChange(contract.charityId, contract._id)

  const validate = useCallback(() => {
    if (recurrenceInterval === initialValue) {
      setValidationError('same_as_before')
      return false
    } else if (!recurrenceInterval) {
      setValidationError('invalid')
      return false
    }

    setValidationError(undefined)
    return true
  }, [recurrenceInterval])

  useEffect(() => {
    validate()
  }, [validate])

  useEffect(() => {
    if (state === 'success') complete()
  }, [state])

  if (error) {
    return (
      <>
        <p className="mb-5 text-red-500">{errorMessages[error]()}</p>
        <FilledButton
          onClick={() => {
            clearError()
          }}
          label={translations.texts.retry.toString()}
        />
      </>
    )
  }

  if (state === 'success') {
    return (
      <>
        <div className="flex flex-col items-center justify-center">
          <CheckCircleIcon className="mr-2 mb-2 w-12 h-12 stroke-emerald-500" />
          <p className="mb-5">
            {translations.texts.change_recurrence_success.toString()}
            {recurrenceInterval && (
              <span className="font-bold">
                {': ' + RecurrenceTranslations.getRecurrenceString(translations, { interval: recurrenceInterval.interval, count: recurrenceInterval.count }).toLowerCase()}
              </span>
            )}
          </p>
        </div>
        <FilledButton onClick={success} label={translations.texts.close.toString()} />
      </>
    )
  }

  return (
    <>
      <ContractRecurrenceInput
        value={recurrenceInterval}
        setValue={setRecurrenceInterval}
        error={validationError && validationError in validationErrors ? validationErrors[validationError as ValidationErrorKeys]() : undefined}
      />
      <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <FilledButton
          onClick={() => {
            if (validate()) void save({ recurrence: recurrenceInterval?.interval, recurrenceCount: recurrenceInterval?.count })
          }}
          label={translations.texts.confirm.toString()}
          disabled={validationError !== undefined}
          loading={state === 'loading'}
        />
        <SimpleButton onClick={cancel} label={translations.texts.cancel.toString()} disabled={state === 'loading'} />
      </div>
    </>
  )
}
